import type { Correlations } from "@/composables/forms/formTypes";
import type {
  BasicData,
  FlowRatePrerequisites,
  GasPercentages,
  GasProperties
} from "./d";
import { DensityUnits } from "@/libs/units/density";
import { getGasPhaseVolumeCompressibility } from "../gasPhase/gasPhaseVolumeCompressibility";
import { calculatePsuedoCriticals } from "../gasPhase/psuedo-criticals";
import { getOilViscosityCorrelations } from "../oilPhase/oilViscosityCalculations";
import { getCondensateViscosityCorrelations } from "../condensatePhase/condensateViscosityCalculations";
import { getPVTFluidProperties, getOilDensityLbmft3 } from "../oilPhasePVT";
import { getGasViscosityCalculations } from "../gasPhase/gasViscosityCalculations";
import { getWaterCalculations } from "../waterPhase";
import {
  Gas_Compressibility_Options_Enum,
  Water_Volume_Factor_Options_Enum,
  Water_Solution_Gas_Options_Enum,
  Water_Density_Options_Enum,
  Well_Gas_Type_Options_Enum
} from "gql/api";
import { get_water_phase_in_situ_viscosity } from "../pressureDropCorrelations";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import {
  emptyCondensatePVT,
  getCondensatePVT
} from "../condensatePhase/condensatePVT";
import { getOvalleGasCondensateRatioScfStb } from "../condensatePhase/condensateProperties";
import { getCondensateDensityLBMFT3 } from "../condensatePhase/condensateDensity";
import { withoutAbsentGasComponents } from "@/libs/calculations/gasPhase/gasTable";
// PVT!D50 — sum of the entered gas composition, as mole fractions.
export function getGasMoleRatio(gasPercentages: GasPercentages): number {
  return (
    Object.values(gasPercentages).reduce(
      (sum, percent) => sum + (percent || 0),
      0
    ) / 100
  );
}

// PVT!O62 branches on D50<=1. Compositions are entered to two decimal places
// and summed in floating point, so an exactly-100% entry can land a few ulp
// above 1.0; this tolerance keeps it on the D50<=1 side. It is deliberately
// far tighter than a rounding slip like 100.01%, which the workbook does treat
// as over 1.
const GAS_MOLE_RATIO_TOLERANCE = 1e-9;

function getOvalleGasCondensateRatioOrZero(
  bottomTubingPressurePSIA: number,
  gasSpecificGravity: number,
  temperatureF: number,
  condensateApi: number | null | undefined
): number {
  if (!condensateApi || condensateApi <= 0) {
    return 0;
  }

  return getOvalleGasCondensateRatioScfStb(
    bottomTubingPressurePSIA,
    gasSpecificGravity,
    temperatureF,
    condensateApi
  );
}

// PVT!O60-or-O61 correlation selection, the inner IF of PVT!O62:
// IF(D50<=1, O60, O61) — a gas composition summing to at most 1 uses Peng
// Robinson's GCR (PVT!O60); an over-entered composition falls back to Ovalle
// (PVT!O61). The GLR cap is applied afterwards in getFlowRatePrerequisites.
//
// O60 is only available when Peng Robinson is the selected compressibility
// correlation, so an unusable O60 falls through to Ovalle the way the older
// revision of the workbook did with IFERROR(O60,O61). Once the gas table is
// evaluated unconditionally (Calc 0) the fallback becomes dead and should go.
//
// A user override beats both.
function getGasCondensateRatio(
  correlations: Correlations,
  gasPercentages: GasPercentages,
  bottomTubingPressurePSIA: number,
  gasSpecificGravity: number,
  temperatureF: number,
  condensateApi: number | null | undefined,
  rsoCorrectedScfStb: number,
  rswCorrectedScfStb: number,
  gasLiquidRatioScfStb: number,
  gasProperties: GasProperties[]
): number {
  const usePengRobinson =
    correlations?.gas_compressibility ===
      Gas_Compressibility_Options_Enum.PengRobinson &&
    getGasMoleRatio(gasPercentages) <= 1 + GAS_MOLE_RATIO_TOLERANCE;

  let gasCondensateRatioScfStb = 0;
  if (usePengRobinson) {
    // PVT!O60 = SUM(Y95:Y108)/SUM(Z95:Z108). Y95 is the Liquid-state surface
    // rate (gasTable's condiScf) and Z95 that rate weighted by the liquid root
    // and converted to stb (gasTable's condiRateStb), so the ratio falls out of
    // the per-component table the same way condensateCompressibilityFactor does.
    // No liquid-phase components means nothing condenses, so report zero rather
    // than NaN/Infinity from a 0/0 division.
    const presentGasProperties = withoutAbsentGasComponents(gasProperties);
    const condiScfTotal = presentGasProperties.reduce(
      (sum, g) => sum + g.condiScf,
      0
    );
    const condiRateStbTotal = presentGasProperties.reduce(
      (sum, g) => sum + g.condiRateStb,
      0
    );
    gasCondensateRatioScfStb =
      condiRateStbTotal > 0 ? condiScfTotal / condiRateStbTotal : 0;
  } else {
    gasCondensateRatioScfStb = getOvalleGasCondensateRatioOrZero(
      bottomTubingPressurePSIA,
      gasSpecificGravity,
      temperatureF,
      condensateApi
    );
  }

  if (
    gasCondensateRatioScfStb + rswCorrectedScfStb + rsoCorrectedScfStb >
    gasLiquidRatioScfStb
  ) {
    return Math.max(
      0,
      gasLiquidRatioScfStb - rsoCorrectedScfStb - rswCorrectedScfStb
    );
  }

  return gasCondensateRatioScfStb;
}

export function getFlowRatePrerequisites(
  basicData: BasicData,
  gasOilRatioSCFSTB: number,
  gasLiquidRatioSCFSTB: number,
  waterCutRatio: number,
  correlations: Correlations,
  pressurePSIA: number,
  temperatureF: number,
  gasProperties: GasProperties[],
  bubblePointPressurePSIAOverride = 0,
  dewPointPressurePSIAOverride = 0,
  customFluidProperties: LagrangeInterpolationMapWithGroupings | null,
  customViscosityProperties: LagrangeInterpolationMapWithGroupings | null,
  condensateApi: number,
  condensateSpecificGravity = 0
): FlowRatePrerequisites {
  const {
    oilAPI,
    gasMolecularWeight,
    waterSpecificGravity,
    gasSpecificGravity
  } = basicData;

  const oilSpecificGravity = DensityUnits.SpecificGravity.toThisUnit(
    DensityUnits.API.toStandardUnit(oilAPI)
  );

  const psuedoCriticals = calculatePsuedoCriticals(
    basicData,
    pressurePSIA,
    temperatureF,
    gasProperties
  );

  // Rso, Pb, Bo
  const {
    solutionGasRso: oilPhaseGasSolubilitySCFSTB,
    solutionGasRsoCorrected: oilPhaseGasSolubilityCorrectedSCFSTB,
    bubblePointPressurePSIA,
    oilPhaseVolumeFactorBo: oilVolumeFactorBBLSTB,
    oilFormationVolumeFactorSatBo,
    oilFormationVolumeFactorUnSatBo
  } = getPVTFluidProperties(
    basicData,
    gasOilRatioSCFSTB,
    correlations,
    pressurePSIA,
    temperatureF,
    condensateApi,
    condensateSpecificGravity,
    bubblePointPressurePSIAOverride,
    customFluidProperties,
    waterCutRatio
  );

  const isWetGas = basicData.gasType === Well_Gas_Type_Options_Enum.Wet;

  const condensatePVT = isWetGas
    ? getCondensatePVT({
        pressurePSIA,
        reservoirPressurePSIA: basicData.staticBottomholePressurePSIA,
        temperatureF,
        gasSpecificGravity,
        condensateSpecificGravity,
        condensateApi,
        gasOilRatio: gasOilRatioSCFSTB,
        gasProperties,
        isPengRobinson:
          correlations?.gas_compressibility ===
          Gas_Compressibility_Options_Enum.PengRobinson,
        hasGasCompositeValues: basicData.hasGasCompositeValues,
        dewPointPressurePSIAOverride
      })
    : emptyCondensatePVT;
  const {
    dewPointPressurePSIA,
    condensatePhaseVolumeFactor: condensateVolumeFactorBBLSTB
  } = condensatePVT;

  const { condensateViscosityCP } = isWetGas
    ? getCondensateViscosityCorrelations(
        basicData,
        gasOilRatioSCFSTB,
        correlations,
        pressurePSIA,
        temperatureF,
        condensateSpecificGravity,
        condensatePVT.solutionGasRso,
        dewPointPressurePSIA
      )
    : { condensateViscosityCP: 0 };

  // gasProperties will be passed here
  const { deadOilViscosityCP, oilViscosityCP } = getOilViscosityCorrelations(
    basicData,
    gasOilRatioSCFSTB,
    correlations,
    pressurePSIA,
    temperatureF,
    condensateSpecificGravity,
    oilPhaseGasSolubilitySCFSTB,
    bubblePointPressurePSIA,
    customViscosityProperties
  );

  // gasProperties will be passed here
  // rho_o — computed here (ahead of its usual position below) because
  // Peng-Robinson's condensate Z (PVT!K68) normalizes against oil density,
  // not Bo (see the oilDensityLBMFT3 comment on GasVolumeCompressibilityParameters).
  const oilDensityLBMFT3 = getOilDensityLbmft3(
    oilAPI,
    oilSpecificGravity,
    gasSpecificGravity,
    oilPhaseGasSolubilitySCFSTB,
    temperatureF,
    correlations?.oil_density,
    oilVolumeFactorBBLSTB,
    pressurePSIA,
    bubblePointPressurePSIA
  );

  const gasCompressibilityData = getGasPhaseVolumeCompressibility(
    basicData,
    correlations,
    psuedoCriticals,
    pressurePSIA,
    temperatureF,
    gasProperties,
    dewPointPressurePSIA,
    oilFormationVolumeFactorSatBo,
    oilFormationVolumeFactorUnSatBo,
    oilDensityLBMFT3
  );
  const { gasCompressibilityFactor, condensateCompressibilityFactor } =
    gasCompressibilityData;

  const { gasState, gasViscosityCP, gasDensityLBMFT3, gasVolumeFactorCFSFCF } =
    getGasViscosityCalculations(
      basicData,
      correlations,
      psuedoCriticals,
      pressurePSIA,
      temperatureF,
      gasMolecularWeight,
      gasCompressibilityFactor,
      gasProperties,
      customFluidProperties,
      customViscosityProperties
    );

  const salinityPPM = correlations?.salinity_ppm ?? 0;

  // waterPhaseGasSolubility is RSW
  const {
    waterDensityLBMFT3,
    waterVolumeFactorBBLSTB,
    waterPhaseGasSolubilitySCFSTB,
    waterPhaseGasSolubilityCorrectedSCFSTB
  } = getWaterCalculations(
    pressurePSIA,
    temperatureF,
    bubblePointPressurePSIA,
    salinityPPM,
    oilAPI,
    waterSpecificGravity,
    gasSpecificGravity,
    gasMolecularWeight,
    gasCompressibilityFactor,
    correlations?.water_volume_factor || Water_Volume_Factor_Options_Enum.Gould,
    correlations?.water_solution_gas || Water_Solution_Gas_Options_Enum.Mccain,
    correlations?.water_density || Water_Density_Options_Enum.Gould,
    gasOilRatioSCFSTB,
    oilPhaseGasSolubilitySCFSTB,
    customFluidProperties,
    gasLiquidRatioSCFSTB,
    oilPhaseGasSolubilityCorrectedSCFSTB,
    waterCutRatio
  );

  // mu_w
  const waterViscosityCP = get_water_phase_in_situ_viscosity(
    temperatureF,
    pressurePSIA,
    waterSpecificGravity,
    correlations?.water_viscosity,
    salinityPPM,
    customViscosityProperties
  );

  // Ovalle's inputs are scenario constants in the workbook, not per-point
  // values: PVT!D15 "Bottom of Tubing Pressure" and PVT!D18 "In Situ
  // Temperature at Datum". Passing the per-point temperature here would make
  // GCR vary down the wellbore, which the sheet does not do.

  let gasCondensateRatioScfStb = 0;
  if (isWetGas) {
    gasCondensateRatioScfStb = getGasCondensateRatio(
      correlations,
      basicData.gasPercentages,
      pressurePSIA,
      gasSpecificGravity,
      temperatureF,
      condensateApi,
      oilPhaseGasSolubilityCorrectedSCFSTB,
      waterPhaseGasSolubilityCorrectedSCFSTB,
      gasLiquidRatioSCFSTB,
      gasProperties
    );
  }

  const condensateDensityLBMFT3 = getCondensateDensityLBMFT3(
    basicData.gasType,
    correlations?.gas_compressibility ===
      Gas_Compressibility_Options_Enum.PengRobinson,
    basicData.hasGasCompositeValues,
    gasProperties,
    condensateSpecificGravity,
    condensateApi,
    gasSpecificGravity,
    oilPhaseGasSolubilitySCFSTB,
    pressurePSIA,
    temperatureF,
    dewPointPressurePSIA
  );

  return {
    // viscosities
    deadOilViscosityCP,
    oilViscosityCP,
    waterViscosityCP,
    gasViscosityCP,
    condensateViscosityCP,
    // densities
    waterDensityLBMFT3,
    oilDensityLBMFT3,
    gasDensityLBMFT3,
    condensateDensityLBMFT3,
    // solubilities
    waterPhaseGasSolubilitySCFSTB,
    waterPhaseGasSolubilityCorrectedSCFSTB,
    oilPhaseGasSolubilitySCFSTB,
    oilPhaseGasSolubilityCorrectedSCFSTB,
    // volume factors
    gasCompressibilityFactor,
    condensateCompressibilityFactor,
    gasCondensateRatioScfStb,
    condensateSpecificGravity,
    condensateApi,
    dewPointPressurePSIA,
    oilVolumeFactorBBLSTB,
    condensateVolumeFactorBBLSTB,
    gasVolumeFactorCFSFCF,
    waterVolumeFactorBBLSTB,
    // other
    psuedoCriticals,
    bubblePointPressurePSIA,
    gasState,
    oilSpecificGravity
  };
}
